<template>
  <div class="ccma-upload-photo">
    <!-- Full-screen loading overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-container">
        <Loader type="infinite-spinner" :size="'large'" />
        <p class="loading-text">{{ t('consultantCorner.uploadPhoto.LoadingPhoto') }}</p>
      </div>
    </div>

    <div v-if="showError" class="ccma-upload-photo-error">
      <h2>{{ t('consultantCorner.uploadPhoto.PageLoadError') }}</h2>
    </div>

    <!-- Error notification for save operations -->
    <div v-if="saveError" class="save-error-notification">
      <div class="error-content">
        <div class="error-icon">⚠️</div>
        <div class="error-details">
          <h3>{{ t('consultantCorner.uploadPhoto.SaveError') }}</h3>
          <p>{{ saveErrorMessage }}</p>
        </div>
        <button class="error-close" @click="clearSaveError">×</button>
      </div>
      <div class="error-actions">
        <button class="baseBtn primaryBtn" @click="savePhoto">{{ t('consultantCorner.uploadPhoto.TryAgain') }}</button>
      </div>
    </div>

    <!-- Success notification -->
    <div v-if="saveSuccess" class="save-success-notification">
      <div class="success-content">
        <div class="success-icon">✅</div>
        <div class="success-message">
          <p>{{ t('consultantCorner.uploadPhoto.SaveSuccess') }}</p>
        </div>
        <button class="success-close" @click="clearSaveSuccess">×</button>
      </div>
    </div>

    <div v-if="!showError && !isLoading">
      <!-- Header Section -->
      <div class="upload-header">
        <div class="first">
          <div class="upload-title">{{ t('consultantCorner.uploadPhoto.UploadPhotoHeader') }}</div>
          <div class="upload-subtitle mb-1">{{ t('consultantCorner.uploadPhoto.UploadPhotoSubHeader') }}</div>
        </div>
        <div class="upload-buttons">
          <button
            id="btnSave"
            class="baseBtn primaryBtn"
            :disabled="!selectedFile || isSaving"
            @click="savePhoto"
          >
            {{ isSaving ? t('consultantCorner.uploadPhoto.Uploading') : t('consultantCorner.uploadPhoto.Save') }}
          </button>
        </div>
      </div>

      <!-- Current Photo Display -->
      <div class="current-photo-section" v-if="currentPhotoUrl">
        <div class="current-photo-label">{{ t('consultantCorner.uploadPhoto.CurrentPhoto') }}</div>
        <div class="current-photo">
          <img :src="currentPhotoUrl" :alt="t('consultantCorner.uploadPhoto.ProfilePhotoAlt')" class="profile-photo" />
        </div>
      </div>

      <!-- Upload Section -->
      <div class="upload-section">
        <div v-if="!selectedFile" class="upload-area" :class="{ 'drag-over': isDragOver }" @dragover.prevent="onDragOver" @dragleave.prevent="onDragLeave" @drop.prevent="onDrop">
          <input
            ref="fileInput"
            type="file"
            @change="onFileSelected"
            accept="image/jpeg,image/png"
            class="file-input"
            id="photo-upload"
          />
          <label for="photo-upload" class="upload-label">
            <div class="upload-content">
              <i class="fas fa-camera"></i>
              <p>{{ t('consultantCorner.uploadPhoto.UploadFile') }}</p>
              <small>{{ t('consultantCorner.uploadPhoto.MaxFileSize') }}</small>
              <small>{{ t('consultantCorner.uploadPhoto.SupportedFormats') }}</small>
            </div>
          </label>
        </div>

        <!-- Selected File Preview -->
        <div v-if="selectedFile" class="selected-file">
          <p><strong>{{ t('consultantCorner.uploadPhoto.SelectedFile') }}:</strong> {{ selectedFile.name }}</p>
          <div class="file-info">
            <span class="file-size">{{ formatFileSize(selectedFile.size) }}</span>
            <span class="file-type">{{ selectedFile.type }}</span>
          </div>
          <div class="file-preview" v-if="previewUrl">
            <img :src="previewUrl" :alt="t('consultantCorner.uploadPhoto.PreviewAlt')" class="preview-image" />
          </div>
          <div v-if="fileError" class="error-message">{{ fileError }}</div>
          <button class="remove-file-btn" @click="removeSelectedFile" type="button">
            {{ t('consultantCorner.uploadPhoto.RemoveFile') }}
          </button>
        </div>

        <!-- Rejected Photos Display -->
        <div v-if="rejectedPhotos && rejectedPhotos.length > 0" class="rejected-photos">
          <h4>{{ t('consultantCorner.uploadPhoto.RejectedPhotos') }}</h4>
          <div v-for="rejected in rejectedPhotos" :key="rejected.beePhotoGuid" class="rejected-photo">
            <img v-if="rejected.uri" :src="rejected.uri" :alt="t('consultantCorner.uploadPhoto.RejectedPhotoAlt')" class="rejected-image" />
            <div class="rejection-reason">
              <strong>{{ t('consultantCorner.uploadPhoto.RejectionReason') }}:</strong> {{ rejected.reason }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { Loader } from '@progress/kendo-vue-indicators'
import {
  PwsProfilePhotoGet,
  PwsProfilePhotoUpload,
  type PwsProfilePhotoGetRequest,
  type PwsProfilePhotoUploadRequest,
  type RejectedPhoto
} from '../../api/ConsultantCorner'
import type { IBusinessDetails } from '../../types/ConsultantCornerTypes'

const { t } = useI18n()
const logger = useLogger()

const props = defineProps<{
  businessDetails: IBusinessDetails | null
}>()

const emit = defineEmits<{
  updateDetails: [details: Partial<IBusinessDetails>]
}>()

// State management
const isLoading = ref(false)
const showError = ref(false)
const saveError = ref(false)
const saveSuccess = ref(false)
const saveErrorMessage = ref('')
const isSaving = ref(false)

// File handling
const selectedFile = ref<File | null>(null)
const previewUrl = ref<string>('')
const fileError = ref<string>('')
const fileInput = ref<HTMLInputElement>()
const currentPhotoUrl = ref<string>('')
const rejectedPhotos = ref<RejectedPhoto[]>([])
const isDragOver = ref(false)

const loadCurrentPhoto = async () => {

  try {
    isLoading.value = true
    showError.value = false

    const request: PwsProfilePhotoGetRequest = {
      beeNumber: props.businessDetails?.beeNumber
    }
    const response = await PwsProfilePhotoGet(request)

    if (response.approvedURI) {
      // The approved photo URL is filename-based, so re-uploading with the same
      // file name yields an identical URL and the browser/CDN serves the cached
      // (old) image. Cache-bust with the photo guid, which changes on every
      // upload, so the URL only changes when the photo actually changes.
      currentPhotoUrl.value = response.beePhotoGuid
        ? `${response.approvedURI}?v=${response.beePhotoGuid}`
        : response.approvedURI
    }

    if (response.rejectedPhoto && response.rejectedPhoto.length > 0) {
      rejectedPhotos.value = response.rejectedPhoto
    }
  } catch (error: any) {
    logger.error('Error loading current photo:', error)

    // Check if this is a BeePhotoNotFoundException (400 response)
    // In this case, simply show the upload section since no photo exists
    if (error?.response?.status === 400 &&
        (error?.response?.statusText === 'BeePhotoNotFoundException' ||
         error?.response?.data?.responseStatus?.errorCode === 'BeePhotoNotFoundException' ||
         error?.message?.includes?.('BeePhotoNotFoundException'))) {
      // Don't show error - just proceed to show upload section
    } else {
      // For other errors, show the error page
      showError.value = true
    }
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  loadCurrentPhoto()
})

// File validation
const validateFile = (file: File): boolean => {
  fileError.value = ''

  const maxSize = 4 * 1024 * 1024 // 4MB
  if (file.size > maxSize) {
    fileError.value = t('consultantCorner.uploadPhoto.FileTooLarge')
    return false
  }

  const allowedTypes = ['image/jpeg', 'image/png']
  if (!allowedTypes.includes(file.type)) {
    fileError.value = t('consultantCorner.uploadPhoto.InvalidFileFormat')
    return false
  }

  return true
}

const createPreview = (file: File) => {
  const reader = new FileReader()
  reader.onload = (e) => {
    previewUrl.value = e.target?.result as string
  }
  reader.readAsDataURL(file)
}

const onFileSelected = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    const file = target.files[0]
    processSelectedFile(file)
  }
}

const processSelectedFile = (file: File) => {
  if (validateFile(file)) {
    selectedFile.value = file
    createPreview(file)
  } else {
    selectedFile.value = null
    previewUrl.value = ''
  }
}

// Drag and drop handlers
const onDragOver = () => {
  isDragOver.value = true
}

const onDragLeave = () => {
  isDragOver.value = false
}

const onDrop = (event: DragEvent) => {
  isDragOver.value = false
  const files = event.dataTransfer?.files
  if (files && files[0]) {
    processSelectedFile(files[0])
  }
}

const removeSelectedFile = () => {
  selectedFile.value = null
  previewUrl.value = ''
  fileError.value = ''
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const normalizeFileName = (fileName: string): string => {
  return fileName.toLowerCase()
}

const savePhoto = async () => {
  if (!selectedFile.value) return

  try {
    isSaving.value = true
    saveError.value = false
    saveErrorMessage.value = ''

    const request: PwsProfilePhotoUploadRequest = {
      fileName: normalizeFileName(selectedFile.value.name),
      file: selectedFile.value,
      skipValidation: true
    }

    const response = await PwsProfilePhotoUpload(request)

    if (response.beePhotoGuid) {
      // Reload the current photo to get the updated URI
      await loadCurrentPhoto()

      // Clear the selected file
      removeSelectedFile()

      // Show success notification
      saveSuccess.value = true
      setTimeout(() => {
        saveSuccess.value = false
      }, 5000)

      emit('updateDetails', { profilePhotoUrl: currentPhotoUrl.value })
    }
  } catch (error) {
    logger.error('Error uploading photo:', error)
    saveError.value = true
    saveErrorMessage.value = error instanceof Error ? error.message : t('consultantCorner.uploadPhoto.UnknownError')
  } finally {
    isSaving.value = false
  }
}

// Error and success handling
const clearSaveError = () => {
  saveError.value = false
  saveErrorMessage.value = ''
}

const clearSaveSuccess = () => {
  saveSuccess.value = false
}
</script>

<style lang="scss" scoped>
.ccma-upload-photo {
  padding: 0;
  margin: 0;
  width: 100%;
  height: 100%;
}

/* Loading overlay styles */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(white, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: $z-loading-overlay;
}

.loading-container {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: $radius-md;
  box-shadow: 0 4px 6px rgba(black, 0.1);
}

.loading-text {
  @include typography-body-large;
  margin-top: 1rem;
  color: #666;
}

/* Error page styles */
.ccma-upload-photo-error {
  padding: 2rem;
  text-align: center;
  color: #dc3545;
}

/* Notification styles */
.save-error-notification,
.save-success-notification {
  position: fixed;
  top: 20px;
  right: 20px;
  max-width: 400px;
  background: white;
  border-radius: $radius-md;
  box-shadow: 0 4px 12px rgba(black, 0.2);
  z-index: $z-notification;
  animation: slideIn 0.3s ease-out;
}

.save-error-notification {
  border-left: 4px solid #dc3545;
}

.save-success-notification {
  border-left: 4px solid #28a745;
}

.error-content,
.success-content {
  display: flex;
  align-items: flex-start;
  padding: 1rem;
}

.error-icon,
.success-icon {
  @include typography-h3;
  margin-right: 0.75rem;
  margin-top: 0.25rem;
}

.error-details,
.success-message {
  flex: 1;
}

.error-details h3 {
  @include typography-body;
  @include font-weight-semibold;
  margin: 0 0 0.5rem 0;
  color: #dc3545;
}

.error-details p,
.success-message p {
  @include typography-body;
  margin: 0;
  color: #666;
}

.error-close,
.success-close {
  @include typography-h3;
  background: none;
  border: none;
  cursor: pointer;
  color: #999999;
  padding: 0;
  margin-left: 0.5rem;
}

.error-close:hover,
.success-close:hover {
  color: #666;
}

.error-actions {
  padding: 0 1rem 1rem 1rem;
  border-top: 1px solid #e9ecef;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
}

/* Header styles */
.upload-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.upload-title {
  @include typography-h3;
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 0.5rem;
}

.upload-subtitle {
  @include typography-body;
  color: #666;
}

.upload-buttons {
  display: flex;
  gap: 0.75rem;
}

/* Current photo section */
.current-photo-section {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: var(--vt-c-white-soft);
  border-radius: $radius-md;
  border: 1px solid #e9ecef;
}

.current-photo-label {
  @include typography-body-large;
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 1rem;
}

.current-photo {
  text-align: center;
}

.profile-photo {
  width: 150px;
  height: 150px;
  border-radius: $radius-circle;
  object-fit: cover;
  border: 3px solid #e67e22;
  box-shadow: 0 4px 8px rgba(black, 0.1);
}

/* Upload section */
.upload-section {
  margin-bottom: 2rem;
}

.upload-area {
  border: 2px dashed #e0e0e0;
  border-radius: $radius-md;
  padding: 3rem 2rem;
  text-align: center;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
  background: #fefefe;
}

.upload-area:hover,
.upload-area.drag-over {
  border-color: #e67e22;
  background: #fff8f0;
}

.file-input {
  display: none;
}

.upload-label {
  cursor: pointer;
  display: block;
}

.upload-content i {
  @include typography-display;
  color: #e67e22;
  margin-bottom: 1rem;
  display: block;
}

.upload-content p {
  @include typography-h4;
  @include font-weight-medium;
  margin-bottom: 0.75rem;
  color: #333;
}

.upload-content small {
  @include typography-body;
  display: block;
  color: #666;
  margin-bottom: 0.25rem;
}

/* Selected file styles */
.selected-file {
  padding: 1.5rem;
  background-color: var(--vt-c-white-soft);
  border-radius: $radius-md;
  border: 1px solid #e9ecef;
  margin-bottom: 1.5rem;
}

.selected-file p {
  @include typography-body;
  margin-bottom: 0.75rem;
}

.file-info {
  @include typography-body;
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  color: #666;
}

.file-size,
.file-type {
  padding: 0.25rem 0.5rem;
  background: #e9ecef;
  border-radius: $radius-sm;
  font-family: monospace;
}

.file-preview {
  text-align: center;
  margin: 1rem 0;
}

.preview-image {
  max-width: 200px;
  max-height: 200px;
  border-radius: $radius-md;
  border: 2px solid #e67e22;
  box-shadow: 0 2px 4px rgba(black, 0.1);
}

.remove-file-btn {
  background: #dc3545;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  @include typography-body;
  border-radius: $radius-sm;
  cursor: pointer;
  margin-top: 0.5rem;
}

.remove-file-btn:hover {
  background: #c82333;
}

/* Rejected photos section */
.rejected-photos {
  margin-top: 2rem;
  padding: 1.5rem;
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: $radius-md;
}

.rejected-photos h4 {
  @include typography-body-large;
  color: #856404;
  margin-bottom: 1rem;
}

.rejected-photo {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background: white;
  border-radius: $radius-lg;
  border: 1px solid #f5c6cb;
}

.rejected-image {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: $radius-lg;
  border: 2px solid #dc3545;
}

.rejection-reason {
  @include typography-body;
  flex: 1;
  color: #721c24;
}

/* Error message styles */
.error-message {
  @include typography-small;
  color: #dc3545;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: $radius-sm;
}

/* Button styles */
.baseBtn {
  @include typography-body;
  @include font-weight-medium;
  padding: 0.75rem 1.5rem;
  border-radius: $button-radius;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s ease;
  text-decoration: none;
  display: inline-block;
  text-align: center;
}


.primaryBtn:disabled {
  background-color: #cccccc;
  border-color: #cccccc;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Animation */
@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Responsive design */
@media (max-width: 768px) {
  .upload-header {
    flex-direction: column;
    gap: 1rem;
  }

  .upload-buttons {
    width: 100%;
  }

  .baseBtn {
    width: 100%;
  }

  .upload-area {
    padding: 2rem 1rem;
  }

  .current-photo-section {
    padding: 1rem;
  }

  .selected-file {
    padding: 1rem;
  }

  .rejected-photo {
    flex-direction: column;
    text-align: center;
  }

  .save-error-notification,
  .save-success-notification {
    left: 10px;
    right: 10px;
    max-width: none;
  }
}

@media (max-width: 480px) {
  .upload-content i {
    @include typography-h2;
  }

  .upload-content p {
    @include typography-body;
  }

  .profile-photo {
    width: 120px;
    height: 120px;
  }

  .preview-image {
    max-width: 150px;
    max-height: 150px;
  }
}
</style>
